import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { PropTypes } from 'prop-types';
import treeChanges from 'tree-changes';
import moment from 'moment';

import { EditOutlined, LaptopOutlined, ReloadOutlined } from '@ant-design/icons';

import { Row, Col, Table, Button, Badge, Card } from 'antd';

import AntDesignTable from '../../Components/AntDesignTable';
import InvDoc03Actions from '../../Stores/InvDoc03/Actions';

import InvDoc03Dialog from './InvDoc03Dialog';

class InvDoc03Table extends React.PureComponent {
  constructor() {
    super();

    this.useOnTableChange = this.useOnTableChange.bind(this);
    this.useShowWorkspace = this.useShowWorkspace.bind(this);

    this.handleSearch = this.handleSearch.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.getDocumentColumns = this.getDocumentColumns.bind(this);
    this.getExpandedColumns = this.getExpandedColumns.bind(this);
    this.showExpandedRow = this.showExpandedRow.bind(this);
  }

  componentDidMount() {
    const { curSiteFlowId, setWorkspaceVisible, resetTimestamp } = this.props;

    if (curSiteFlowId > 0) {
      setWorkspaceVisible(false);
      resetTimestamp();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      curSiteFlowId,
      resetTimestamp,
      fetchInvDoc03,
      currentPage,
      sorts,
      filters,
      pageSize
    } = this.props;

    const { changed } = treeChanges(prevProps, this.props);

    if (changed('curSiteFlowId')) {
      resetTimestamp();
    }

    if (changed('timestamp')) {
      if (curSiteFlowId > 0) {
        fetchInvDoc03(currentPage, sorts, filters, pageSize);
      }
    }
  }

  componentWillUnmount() {}

  handleSearch(selectedKeys, confirm) {
    // eslint-disable-next-line no-unused-vars
    const { intl } = this.props;

    confirm();
  }

  handleReset(clearFilters) {
    // eslint-disable-next-line no-unused-vars
    const { intl } = this.props;

    clearFilters();
  }

  getDocumentColumns() {
    const { intl, sorts, filters } = this.props;

    return [
      {
        align: 'left',
        title: intl.formatMessage({ id: 'doc_code' }),
        // sort field
        dataIndex: 'doc_code',
        ...AntDesignTable.getColumnSortProps(sorts, 'doc_code'),
        // filter field
        key: 'doc_code',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'doc_code' }),
          'doc_code',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => <>{record.doc_code}</>
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'doc_date' }),
        // sort field
        dataIndex: 'doc_date',
        ...AntDesignTable.getColumnSortProps(sorts, 'doc_date'),
        // filter field
        key: 'doc_date',
        ...AntDesignTable.getColumnSearchDateProps(
          filters,
          intl.formatMessage({ id: 'doc_date' }),
          'doc_date',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => <>{moment(record.doc_date).format('YYYY-MM-DD')}</>
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'doc_status' }),
        // sort field
        dataIndex: 'str_doc_status',
        ...AntDesignTable.getColumnSortProps(sorts, 'doc_status'),
        // filter field
        ...AntDesignTable.getColumnSearchDocStatusProps(filters, 'doc_status'),
        render: (text, record) => <>{record.str_doc_status}</>
      },

      {
        align: 'right',
        title: intl.formatMessage({ id: 'receipt_qty' }),
        // sort field
        dataIndex: 'rcpt_unit_qty',
        ...AntDesignTable.getColumnSortProps(sorts, 'rcpt_unit_qty'),
        // filter field
        key: 'rcpt_unit_qty',
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.rcpt_unit_qty)}
          </>
        )
      },
      {
        align: 'right',
        title: intl.formatMessage({ id: 'order_qty' }),
        // sort field
        dataIndex: 'ord_unit_qty',
        ...AntDesignTable.getColumnSortProps(sorts, 'ord_unit_qty'),
        // filter field
        key: 'ord_unit_qty',
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.ord_unit_qty)}
          </>
        )
      }
    ];
  }

  getExpandedColumns() {
    const { intl, goToOrderDoc } = this.props;

    return [
      {
        align: 'left',
        title: intl.formatMessage({ id: 'code' }),
        // sort field
        dataIndex: 'doc_code',
        // filter field
        key: 'doc_code',
        render: (text, record) => record.doc_code
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'description' }),
        // sort field
        dataIndex: 'delivery_point_company_name_01',
        // filter field
        key: 'delivery_point_company_name_01',
        render: (text, record) => <>{record.delivery_point_company_name_01}</>
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'ref_code' }),
        // sort field
        dataIndex: 'ref_code',
        // filter field
        key: 'ref_code',
        render: (text, record) => (
          <>
            {record.ref_code_01}
            {record.ref_code_02 ? (
              <>
                <br />
                {record.ref_code_02}
              </>
            ) : (
              ''
            )}
            {record.ref_code_03 ? (
              <>
                <br />
                {record.ref_code_03}
              </>
            ) : (
              ''
            )}
            {record.ref_code_04 ? (
              <>
                <br />
                {record.ref_code_04}
              </>
            ) : (
              ''
            )}
          </>
        )
      },
      {
        align: 'left',
        title: intl.formatMessage({ id: 'division' }),
        // sort field
        dataIndex: 'division_code',
        // filter field
        key: 'division_code',
        render: (text, record) => <>{record.division_code}</>
      },

      {
        align: 'right',
        title: intl.formatMessage({ id: 'receipt_qty' }),
        // sort field
        dataIndex: 'rcpt_qty',
        // filter field
        key: 'rcpt_qty',
        render: (text, record) => (
          <>
            {record.rcpt_qty
              ? new Intl.NumberFormat([], {
                  style: 'decimal',
                  minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
                  maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
                }).format(record.rcpt_qty)
              : ''}
          </>
        )
      },
      {
        align: 'right',
        title: intl.formatMessage({ id: 'order_qty' }),
        // sort field
        dataIndex: 'unit_qty',
        // filter field
        key: 'unit_qty',
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.unit_qty)}
          </>
        )
      },

      {
        key: 'action',
        render: (text, record) => (
          <>
            {record.rcpt_qty ? (
              ''
            ) : (
              <Button
                type="dashed"
                icon={<EditOutlined />}
                onClick={() => goToOrderDoc(record.hdr_id, record.item_id)}
              />
            )}
          </>
        )
      }
    ];
  }

  showExpandedRow(record) {
    const { fetchIsLoading } = this.props;
    const finalTreeDetails = record.details.reduce((treeDetails, detail) => {
      // check if treeDetails already have itemId
      let foundTreeDetail = treeDetails.reduce((acc, data) => {
        if (data.item_id === detail.item_id) {
          return data;
        }
        return acc;
      }, null);

      if (foundTreeDetail === null) {
        foundTreeDetail = {
          ...detail,
          id: `item${detail.item_id}`,
          doc_code: detail.item_code,
          delivery_point_company_name_01: detail.desc_01,
          delivery_point_area_code: '',
          salesman_username: '',
          division_code: '',
          rcpt_qty: detail.rcpt_item_ttl_unit_qty,
          unit_qty: detail.unit_qty,
          children: [detail]
        };
        treeDetails.push(foundTreeDetail);
      } else {
        // this item is already in the treeDetails, so just add to total unit_qty,
        // and add detail to children
        const index = treeDetails.indexOf(foundTreeDetail);
        foundTreeDetail = {
          ...foundTreeDetail,
          unit_qty: parseFloat(foundTreeDetail.unit_qty) + parseFloat(detail.unit_qty)
        };
        foundTreeDetail.children.push(detail);

        if (index !== -1) {
          // replace the original treeDetail with updated treeDetail
          treeDetails.splice(index, 1, foundTreeDetail);
        }
      }

      return treeDetails;
    }, []);

    return (
      <Table
        size="small"
        // rowSelection={rowSelection}
        rowKey="id"
        bordered={false}
        pagination={false}
        columns={this.getExpandedColumns()}
        dataSource={finalTreeDetails}
        loading={fetchIsLoading}
        defaultExpandAllRows
        rowClassName={rowData => {
          if (rowData.rcpt_qty && parseFloat(rowData.rcpt_qty) !== parseFloat(rowData.unit_qty)) {
            return 'error-row';
          }
          return '';
        }}
      />
    );
  }

  useOnTableChange(pagination, filters, sorter) {
    const { resetTimestamp } = this.props;

    const processedFilters = AntDesignTable.processFilters(filters);
    const processedSorts = AntDesignTable.processSorts(sorter);

    resetTimestamp(pagination.current, processedSorts, processedFilters, pagination.pageSize);
  }

  useShowWorkspace() {
    const { setWorkspaceVisible } = this.props;

    setWorkspaceVisible(true);
  }

  render() {
    const {
      intl,
      resetTimestamp,
      currentPage,
      pageSize,
      total,
      documents,
      fetchIsLoading,
      selectedDocuments,
      addSelectedDocuments,
      removeSelectedDocuments,
      expandedRows,
      setExpandedRows
    } = this.props;

    return (
      <Card
        title={intl.formatMessage({ id: 'goods_receipts' })}
        bordered={false}
        headStyle={{ textAlign: 'center' }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          size="small"
          // rowSelection={rowSelection}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: parseInt(pageSize, 10),
            total,
            showTotal: () => `${total} items`
          }}
          columns={this.getDocumentColumns()}
          dataSource={documents}
          loading={fetchIsLoading}
          bordered
          rowSelection={{
            selectedRowKeys: selectedDocuments.map(value => value.id),
            onChange: () => {
              // console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
            },
            onSelect: (record, selected) => {
              // console.log(`onSelect record: `, record, 'selected: ', selected, 'selectedRows: ', selectedRows);
              if (selected === true) {
                addSelectedDocuments([record]);
              } else {
                removeSelectedDocuments([record]);
              }
            },
            onSelectAll: (selected, selectedRows, changeRows) => {
              // console.log('onSelectAll selected: ', selected, 'selectedRows: ', selectedRows, 'changeRows: ', changeRows);
              if (selected === true) {
                addSelectedDocuments(changeRows);
              } else {
                removeSelectedDocuments(changeRows);
              }
            },
            onSelectInvert: selectedRows => {
              // console.log('onSelectInvert selectedRows: ', selectedRows);
              removeSelectedDocuments(selectedRows);
            },
            getCheckboxProps: record => ({
              // Column configuration not to be checked
              disabled: record.str_doc_status !== 'COMPLETE'
              // name: record.name,
            })
          }}
          onChange={this.useOnTableChange}
          rowClassName={rowData => {
            if (parseFloat(rowData.rcpt_unit_qty) !== parseFloat(rowData.ord_unit_qty)) {
              return 'error-row';
            }
            return '';
          }}
          expandedRowRender={this.showExpandedRow}
          expandedRowKeys={expandedRows}
          onExpandedRowsChange={rowKeys => {
            setExpandedRows(rowKeys);
          }}
          title={() => (
            <>
              <Row type="flex" justify="space-between" gutter={[0, 16]}>
                <Col span={8}>
                  <Button
                    type="primary"
                    // disabled={!isValid}
                    loading={fetchIsLoading}
                    onClick={() => resetTimestamp()}
                    icon={<ReloadOutlined />}
                  />
                </Col>
              </Row>
            </>
          )}
        />

        <div
          style={{
            position: 'fixed',
            bottom: '50px',
            right: '50px',
            zIndex: 9999
          }}
        >
          <Badge count={selectedDocuments.length}>
            <Button
              size="large"
              type="primary"
              shape="circle"
              icon={<LaptopOutlined />}
              onClick={this.useShowWorkspace}
              loading={fetchIsLoading}
            />
          </Badge>
        </div>

        <InvDoc03Dialog />
      </Card>
    );
  }
}

InvDoc03Table.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  intl: PropTypes.object,
  resetTimestamp: PropTypes.func,

  setWorkspaceVisible: PropTypes.func,

  addSelectedDocuments: PropTypes.func,
  removeSelectedDocuments: PropTypes.func,
  documents: PropTypes.arrayOf(PropTypes.object),
  selectedDocuments: PropTypes.arrayOf(PropTypes.object),

  sorts: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  filters: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),

  currentPage: PropTypes.number,
  pageSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.number,
  fetchIsLoading: PropTypes.bool,

  curSiteFlowId: PropTypes.number,
  fetchInvDoc03: PropTypes.func,
  goToOrderDoc: PropTypes.func,

  expandedRows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  setExpandedRows: PropTypes.func
};

InvDoc03Table.defaultProps = {
  intl: {},
  resetTimestamp() {},

  setWorkspaceVisible() {},

  addSelectedDocuments() {},
  removeSelectedDocuments() {},
  documents: [],
  selectedDocuments: [],

  sorts: {},
  filters: {},

  currentPage: 1,
  pageSize: '20',
  total: 0,
  fetchIsLoading: false,

  curSiteFlowId: 0,
  fetchInvDoc03() {},
  goToOrderDoc() {},

  expandedRows: [],
  setExpandedRows() {}
};

const mapStateToProps = state => ({
  timestamp: state.invDoc03.timestamp,
  workspaceIsVisible: state.invDoc03.workspaceIsVisible,

  documents: state.invDoc03.documents,
  selectedDocuments: state.invDoc03.selectedDocuments,

  sorts: state.invDoc03.sorts,
  filters: state.invDoc03.filters,

  currentPage: state.invDoc03.currentPage,
  pageSize: state.invDoc03.pageSize,
  total: state.invDoc03.total,
  fetchIsLoading: state.invDoc03.fetchIsLoading,

  curSiteFlowId: state.app.curSiteFlowId,
  expandedRows: state.invDoc03.expandedRows
});

const mapDispatchToProps = dispatch => ({
  resetTimestamp: (currentPage, sorts, filters, pageSize) =>
    dispatch(InvDoc03Actions.invDoc03ResetTimestamp(currentPage, sorts, filters, pageSize)),
  setWorkspaceVisible: boolean => dispatch(InvDoc03Actions.invDoc03SetWorkspaceVisible(boolean)),

  addSelectedDocuments: selectedDocuments =>
    dispatch(InvDoc03Actions.invDoc03AddSelectedDocuments(selectedDocuments)),
  removeSelectedDocuments: selectedDocuments =>
    dispatch(InvDoc03Actions.invDoc03RemoveSelectedDocuments(selectedDocuments)),

  fetchInvDoc03: (currentPage, sorts, filters, pageSize) =>
    dispatch(InvDoc03Actions.invDoc03FetchInvDoc03(currentPage, sorts, filters, pageSize)),

  setExpandedRows: expandedRows => dispatch(InvDoc03Actions.invDoc03SetExpandedRows(expandedRows)),

  goToOrderDoc: (hdrId, itemId) => dispatch(InvDoc03Actions.invDoc03GoToOrderDoc(hdrId, itemId))
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(InvDoc03Table));
